import {
	ArrowDown,
	History,
	MousePointer,
	PaintBucket,
	Palette,
	Pencil,
	Play,
	Square,
	Plus,
	Redo2,
	RotateCcw,
	Settings,
	Tag,
	Undo2,
	UserPlus,
} from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { DrawingState, HashAlignment, Tool } from '@/types'
import type { SaveStatus } from '@/contexts/SaveStatusContext'
import { eventBus } from '@/services'
import { useDialogAutoClose } from '@/hooks'
import { ColorPickerDialog } from './dialogs/ColorPickerDialog'
import { DrawOptionsDialog } from './dialogs/DrawOptionsDialog'
import { EraseDialog } from './dialogs/EraseDialog'
import { DrawingDialog } from './dialogs/DrawingDialog'
import { HashDialog } from './dialogs/HashDialog'
import { UnifiedSettingsDialog } from '../shared/UnifiedSettingsDialog'
import { TooltipProvider } from '../ui/Tooltip'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '../ui/AlertDialog'
import { ToolbarButton } from '../ui/ToolbarButton'
import { SidebarToggleButton } from '../ui/SidebarToggleButton'
import { EraserIcon } from './icons/EraserIcon'
import { HashIcon } from './icons/HashIcon'
import { ColorSwatchIndicator } from './ColorSwatchIndicator'
import { SaveStatusIndicator } from './SaveStatusIndicator'
import { areLinemenAtDefaultPositions } from '@/utils/Lineman.utils'
import type { Player } from '@/types/Player.types'
import './toolbar.css'

interface ToolbarProps {
	drawingState: DrawingState
	setDrawingState: (_state: DrawingState) => void
	hashAlignment: HashAlignment
	setHashAlignment: (_alignment: HashAlignment) => void
	showPlayBar: boolean
	setShowPlayBar: (_show: boolean) => void
	playId?: string
	onAnimate?: () => void
	isAnimating?: boolean
	onUndo?: () => void
	onRedo?: () => void
	canUndo?: boolean
	canRedo?: boolean
	players?: Player[]
	saveStatus?: SaveStatus
	onRevertSession?: () => void
	hasSnapshot?: boolean
}

/**
 * Primary toolbar for play editor controls and dialogs.
 */
export function Toolbar({
							drawingState,
							setDrawingState,
							hashAlignment,
							setHashAlignment,
							showPlayBar,
							setShowPlayBar,
							playId: _playId,
							onAnimate,
							isAnimating = false,
							onUndo,
							onRedo,
							canUndo = true,
							canRedo = false,
							players = [],
							saveStatus = 'idle',
							onRevertSession,
							hasSnapshot = false,
						}: ToolbarProps) {
	const [showColorPicker, setShowColorPicker] = useState(false)
	const [showDrawOptions, setShowDrawOptions] = useState(false)
	const [showEraseDialog, setShowEraseDialog] = useState(false)
	const [showDrawingDialog, setShowDrawingDialog] = useState(false)
	const [showClearConfirm, setShowClearConfirm] =
		useState(false)
	const [showHashDialog, setShowHashDialog] = useState(false)
	const [showSettingsDialog, setShowSettingsDialog] =
		useState(false)
	const [columnCount, setColumnCount] = useState(1)
	const toolbarRef = useRef<HTMLDivElement>(null)
	const _drawDialogRef = useRef<HTMLDivElement>(null)

	function _handleSnapThresholdChange(value: number) {
		setDrawingState({ ...drawingState, snapThreshold: value })
	}

	/**
	 * Close all toolbar dialogs except the clear confirmation modal.
	 * Call this before opening any dialog to ensure only one is open at a time.
	 */
	function closeAllDialogs() {
		setShowColorPicker(false)
		setShowDrawOptions(false)
		setShowEraseDialog(false)
		setShowDrawingDialog(false)
		setShowHashDialog(false)
		setShowSettingsDialog(false)
	}

	const handleToolChange = useCallback((tool: Tool) => {
		// Close all dialogs when switching tools
		closeAllDialogs()

		if (tool == 'color') {
			setShowColorPicker(true)
			return
		}

		if (tool == 'draw') {
			// If already on draw tool, toggle the dialog (for keyboard shortcut support)
			if (drawingState.tool == 'draw') {
				setShowDrawOptions(prev => !prev)
			} else {
				setDrawingState({ ...drawingState, tool })
				setShowDrawOptions(true)
			}
			return
		}

		if (tool == 'drawing') {
			setShowDrawingDialog(true)
			return
		}

		setDrawingState({ ...drawingState, tool })
	}, [drawingState, setShowColorPicker, setShowDrawOptions, setShowDrawingDialog, setDrawingState])

	// Auto-close dialogs when cursor moves away
	useDialogAutoClose({
		isOpen: showDrawOptions,
		onClose: () => setShowDrawOptions(false),
		dataAttribute: 'data-draw-dialog',
	})

	useDialogAutoClose({
		isOpen: showColorPicker,
		onClose: () => setShowColorPicker(false),
		dataAttribute: 'data-color-dialog',
	})

	useDialogAutoClose({
		isOpen: showHashDialog,
		onClose: () => setShowHashDialog(false),
		dataAttribute: 'data-hash-dialog',
	})

	useDialogAutoClose({
		isOpen: showSettingsDialog,
		onClose: () => setShowSettingsDialog(false),
		dataAttribute: 'data-settings-dialog',
	})

	useDialogAutoClose({
		isOpen: showDrawingDialog,
		onClose: () => setShowDrawingDialog(false),
		dataAttribute: 'data-drawing-dialog',
	})

	// Listen for keyboard shortcut event
	useEffect(() => {
		const handleDrawToolTrigger = () => {
			handleToolChange('draw')
		}

		eventBus.on('dialog:openDraw', handleDrawToolTrigger)
		return () => eventBus.off('dialog:openDraw', handleDrawToolTrigger)
	}, [drawingState.tool, handleToolChange])

	// Listen for color picker keyboard shortcut
	useEffect(() => {
		const handleColorPickerTrigger = () => {
			// Close all other dialogs first
			closeAllDialogs()

			// Toggle color picker
			setShowColorPicker(prev => !prev)
		}

		eventBus.on('dialog:openColorPicker', handleColorPickerTrigger)
		return () => eventBus.off('dialog:openColorPicker', handleColorPickerTrigger)
	}, [])

	// Listen for drawing tool keyboard shortcut
	useEffect(() => {
		const handleRouteToolTrigger = () => {
			// Close all other dialogs first
			closeAllDialogs()

			// Toggle drawing dialog
			setShowDrawingDialog(prev => !prev)
		}

		eventBus.on('dialog:openDrawing', handleRouteToolTrigger)
		return () => eventBus.off('dialog:openDrawing', handleRouteToolTrigger)
	}, [])

	// Listen for hash dialog keyboard shortcut
	useEffect(() => {
		const handleHashDialogTrigger = () => {
			// Close all other dialogs first
			closeAllDialogs()

			// Toggle hash dialog
			setShowHashDialog(prev => !prev)
		}

		eventBus.on('dialog:openHash', handleHashDialogTrigger)
		return () => eventBus.off('dialog:openHash', handleHashDialogTrigger)
	}, [])

	// Listen for close all dialogs event
	useEffect(() => {
		const handleCloseAllDialogsEvent = () => {
			closeAllDialogs()
		}

		eventBus.on('dialog:closeAll', handleCloseAllDialogsEvent)
		return () => eventBus.off('dialog:closeAll', handleCloseAllDialogsEvent)
	}, [])

	// Calculate optimal column count based on available height
	useEffect(() => {
		const calculateLayout = () => {
			const BUTTON_SIZE = 56
			const GAP = 12
			const PADDING = 12
			const BUTTON_COUNT = 18
			const SEPARATOR_COUNT = 3

			// Use actual toolbar element height when available, fall back to window
			const containerHeight = toolbarRef.current?.clientHeight ?? window.innerHeight
			const availableHeight = containerHeight - (2 * PADDING)

			// How many rows can fit in the available height
			const maxRows = Math.floor((availableHeight + GAP) / (BUTTON_SIZE + GAP))

			// Find minimum columns where rows needed <= maxRows
			// Separators always take 1 full row (they span all columns)
			let columns = 1
			while (columns < 3) {
				const rowsNeeded = Math.ceil(BUTTON_COUNT / columns) + SEPARATOR_COUNT
				if (rowsNeeded <= maxRows) break
				columns++
			}

			setColumnCount(columns)
		}

		calculateLayout()

		window.addEventListener('resize', calculateLayout)
		return () => window.removeEventListener('resize', calculateLayout)
	}, [])

	function handleAddPlayer() {
		eventBus.emit('player:add', {})
	}

	function handleAddComponent() {
		eventBus.emit('component:add')
	}

	function handleToggleTags() {
		closeAllDialogs()
		eventBus.emit('labels:openDialog')
	}

	function handleClearPlay() {
		setShowClearConfirm(true)
	}

	function confirmClearPlay() {
		eventBus.emit('canvas:clear')
		setShowClearConfirm(false)
	}

	return (
		<TooltipProvider>
			<div
				ref={toolbarRef}
				className="editor-toolbar toolbar"
				style={{
					gridTemplateColumns: `repeat(${columnCount}, 56px)`,
					gridAutoRows: '56px',
					gridAutoFlow: 'row',
				}}
			>
				{/* Select Tool */}
				<ToolbarButton
					icon={MousePointer}
					tooltip="Select (S)"
					isActive={drawingState.tool == 'select'}
					onClick={() => handleToolChange('select')}
				/>

				{/* Add Player Tool */}
				<ToolbarButton
					icon={UserPlus}
					tooltip="Add Player (A)"
					isActive={drawingState.tool == 'addPlayer'}
					onClick={() => {
						handleToolChange('addPlayer')
						handleAddPlayer()
					}}
				/>

				{/* Draw Tool - Custom due to status dot */}
				<ToolbarButton
					icon={Pencil}
					tooltip="Draw (D)"
					isActive={drawingState.tool == 'draw'}
					onClick={() => handleToolChange('draw')}
					className={drawingState.tool == 'draw' ? 'toolbar-button-relative' : ''}
				>
					{drawingState.tool == 'draw' && (
						<div className="toolbar-indicator-dot" />
					)}
				</ToolbarButton>

				{/* Erase Tool */}
				<ToolbarButton
					icon={EraserIcon}
					tooltip="Erase (E)"
					isActive={drawingState.tool == 'erase'}
					onClick={() => {
						// Close all other dialogs first
						const wasOpen = showEraseDialog
						closeAllDialogs()

						// If already on erase tool, toggle the dialog
						if (drawingState.tool == 'erase') {
							setShowEraseDialog(!wasOpen)
						} else {
							// Switch to erase tool and show dialog
							setDrawingState({ ...drawingState, tool: 'erase' })
							setShowEraseDialog(true)
						}
					}}
				/>

				{/* Color Tool - Custom with color swatch overlay */}
				<ToolbarButton
					icon={Palette}
					tooltip="Pick Color (C)"
					isActive={showColorPicker}
					onClick={() => handleToolChange('color')}
					className="toolbar-button-relative"
				>
					<ColorSwatchIndicator color={drawingState.color} />
				</ToolbarButton>

				{/* Fill Color Tool */}
				<ToolbarButton
					icon={PaintBucket}
					iconStyle={{ transform: 'scaleX(-1)' }}
					tooltip="Fill Color (F)"
					isActive={drawingState.tool == 'fill'}
					onClick={() => handleToolChange('fill')}
				/>

				<div className="toolbar-separator" />

				{/* Undo Button */}
				<ToolbarButton
					icon={Undo2}
					tooltip="Undo (⌘Z)"
					onClick={onUndo}
					disabled={!canUndo || !onUndo}
				/>

				{/* Redo Button */}
				<ToolbarButton
					icon={Redo2}
					tooltip="Redo (⌘⇧Z)"
					onClick={onRedo}
					disabled={!canRedo || !onRedo}
				/>

				<div className="toolbar-separator" />

				{/* Drawing Tool */}
				<ToolbarButton
					icon={ArrowDown}
					iconStyle={{ transform: 'rotate(-45deg)' }}
					tooltip="Add Drawing (R)"
					isActive={showDrawingDialog}
					onClick={() => handleToolChange('drawing')}
				/>

				{/* Hash Marker Tool */}
				<ToolbarButton
					icon={HashIcon}
					tooltip="Ball on Hash (H)"
					isActive={showHashDialog}
					onClick={() => {
						closeAllDialogs()
						setShowHashDialog(!showHashDialog)
					}}
				/>

				{/* Create Concept Tool */}
				<ToolbarButton
					icon={Plus}
					tooltip="Create Concept (G)"
					isActive={drawingState.tool == 'addComponent'}
					onClick={() => {
						handleToolChange('addComponent')
						handleAddComponent()
					}}
				/>

				{/* Labels Button */}
				<ToolbarButton
					icon={Tag}
					tooltip="Labels (T)"
					onClick={handleToggleTags}
				/>

				<div className="toolbar-separator" />

				{/* Animate Button */}
				{onAnimate && (
					<ToolbarButton
						icon={isAnimating ? Square : Play}
						tooltip={isAnimating ? 'Stop' : 'Animate'}
						onClick={onAnimate}
						isActive={isAnimating}
					/>
				)}

				{/* Toggle Play Bar Button */}
				<SidebarToggleButton
					isVisible={showPlayBar}
					onToggle={() => setShowPlayBar(!showPlayBar)}
				/>

				{/* Settings Button */}
				<ToolbarButton
					icon={Settings}
					tooltip="Settings"
					onClick={() => {
						closeAllDialogs()
						setShowSettingsDialog(!showSettingsDialog)
					}}
				/>

				{/* Clear Canvas Button */}
				<ToolbarButton
					icon={RotateCcw}
					tooltip="Clear Canvas"
					onClick={handleClearPlay}
				/>

				{/* Save Status Indicator */}
				<SaveStatusIndicator status={saveStatus} />

				{/* Revert Session Button */}
				<ToolbarButton
					icon={History}
					tooltip="Revert Session"
					onClick={onRevertSession}
					disabled={!hasSnapshot}
				/>
			</div>

			{/* Dialogs */}
			{showEraseDialog && (
				<EraseDialog
					eraseSize={drawingState.eraseSize}
					onEraseSizeChange={(eraseSize) => {
						setDrawingState({ ...drawingState, tool: 'erase', eraseSize })
					}}
					onClose={() => setShowEraseDialog(false)}
				/>
			)}

			{showColorPicker && (
				<ColorPickerDialog
					currentColor={drawingState.color}
					onColorChange={(color) =>
						setDrawingState({ ...drawingState, color })
					}
					onClose={() => setShowColorPicker(false)}
				/>
			)}

			{showDrawOptions && drawingState.tool == 'draw' && (
				<DrawOptionsDialog
					lineStyle={drawingState.lineStyle}
					lineEnd={drawingState.lineEnd}
					brushSize={drawingState.brushSize}
					pathMode={drawingState.pathMode}
					onLineStyleChange={(lineStyle) =>
						setDrawingState({ ...drawingState, lineStyle })
					}
					onLineEndChange={(lineEnd) =>
						setDrawingState({ ...drawingState, lineEnd })
					}
					onBrushSizeChange={(brushSize) =>
						setDrawingState({ ...drawingState, brushSize })
					}
					onPathModeChange={(pathMode) =>
						setDrawingState({ ...drawingState, pathMode })
					}
					onClose={() => setShowDrawOptions(false)}
				/>
			)}

			{showDrawingDialog && (
				<DrawingDialog
					onClose={() => setShowDrawingDialog(false)}
				/>
			)}

			<AlertDialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Clear Play?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to clear the current play? This action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={confirmClearPlay}
							data-variant="destructive"
						>
							Clear
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{showHashDialog && (
				<HashDialog
					currentAlignment={hashAlignment}
					linemenAtDefault={areLinemenAtDefaultPositions(players, hashAlignment)}
					onAlignmentChange={setHashAlignment}
					onClose={() => setShowHashDialog(false)}
				/>
			)}

			<UnifiedSettingsDialog
				isOpen={showSettingsDialog}
				onClose={() => setShowSettingsDialog(false)}
				context="play-editor"
			/>
		</TooltipProvider>
	)
}